import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import styled from 'styled-components';
import {FmlxIcon} from 'fmlx-common-ui';
import {useDispatch, useSelector} from 'react-redux';
import {find} from '../../../lib/objectHelper';

import LabwarePreview from './LabwarePreview';

import {WellIncrementPattern} from '../../../constants/dataEnum';
import {isColorLight} from '../../../lib/colorHelper';

import './UploadAndManageCSVFile.scss';
import {
  removeNormalizationCSVFile,
  showNormalizationInvalidFile,
} from '../../../stores/business/taskListNormalizationBusinessAction';
import {isCsvNormalizationValid} from '../../../lib/normalizationHelper';

const getTextColor = (colorHex) => {
  try {
    return isColorLight(colorHex) ? '#000000' : '#ffffff';
  } catch (err) {
    return '#000000';
  }
};

const getBackgroundColor = (status, opacity = 1) => {
  let color = '#fff';

  if (status) {
    const opacityHex = Math.ceil(opacity * 255).toString(16);
    color = `#006B2E${opacityHex}`;
  }

  return color;
};

const TableCell = styled.td`
  font-weight: ${(prop) => (prop.highlightData ? 'bold' : 'unset')};
  font-size: ${(prop) => (prop.highlightData ? '18px' : 'unset')};
  background: ${(prop) => {
    const opacity = prop.level ? prop.level : 1;

    if (prop.isHeader) {
      return '#E0E0E0';
    }

    if (prop.isInvalid) {
      return '#ffd1d1';
    }

    return getBackgroundColor(prop.selected, opacity);
  }};
  color: ${(prop) => {
    let color = '#000000';

    if (prop.selected) {
      const opacity = prop.level ? prop.level : 1;
      color = getTextColor(getBackgroundColor(prop.selected, opacity));
    }
    return color;
  }};
  border-left: ${(prop) => (prop.selected ? '2px solid #338CFF' : '2px solid black')};
`;

function UploadAndManageCSVFile({
  handleChangeWellIncrementPattern,
  onUploadNormalizationCSVFile,
  handleChangeDelimiter,
  handleSelectRowColumnIndex,
  appStateAction,
  globalAddUndoItem,
}) {
  const {normalizationState, labwareState} = useSelector((state) => state);
  const {
    wellIncrementPattern,
    csvData,
    csvRawData,
    delimiter,
    csvFileName,
    sampleLabwareID,
    selectedColumnIndex,
    startRow,
  } = normalizationState;

  const selectedLabware = useMemo(
    () => find(labwareState, {LabwareID: sampleLabwareID}) ?? null,
    [labwareState, sampleLabwareID],
  );

  const [currentDelimiter, setCurrentDelimiter] = useState(delimiter);
  const [tableCellSize, setTableCellSize] = useState(null);
  const [selectedLabwareWell, setSelectedLabwareWell] = useState();

  const dispatch = useDispatch();

  const hasHederRef = useRef(null);
  const fileNameRef = useRef(csvFileName);
  const tableWrapperRef = useRef(null);
  const tableCellRef = useRef(null);

  useEffect(() => {
    if (tableCellRef.current && !tableCellSize) {
      const {height, width} = tableCellRef.current.getBoundingClientRect();

      setTableCellSize({height, width});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumnIndex]);

  useEffect(() => {
    if (tableCellSize) {
      tableWrapperRef.current.scrollLeft = (selectedColumnIndex - 2) * tableCellSize.width;
      tableWrapperRef.current.scrollTop = startRow * tableCellSize.height;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableCellSize]);

  const handleClickLabwareWell = (index) => {
    if (tableWrapperRef.current && selectedColumnIndex && tableCellSize) {
      if (selectedLabwareWell !== index) {
        const hasHeader = hasHederRef.current;
        tableWrapperRef.current.scrollTop = (startRow + index - (hasHeader ? 1 : 0)) * tableCellSize.height;
        tableWrapperRef.current.scrollLeft = (selectedColumnIndex - 2) * tableCellSize.width;
        setSelectedLabwareWell(index);
      } else {
        setSelectedLabwareWell(null);
      }
    }
  };

  const handleLoadStart = (event) => {
    console.log('fileReader.onloadstart -> timeStamp:', event.timeStamp);

    appStateAction.showLoading('Loading CSV File...');
  };

  const handleLoad = (event) => {
    appStateAction.hideLoading();

    const data = Papa.parse(event.target.result, {
      delimiter: normalizationState.delimiter,
      dynamicTyping: true,
      skipEmptyLines: 'greedy',
    });

    const isCsvValid = isCsvNormalizationValid(data);

    if (!isCsvValid) {
      dispatch(showNormalizationInvalidFile());
      return;
    }

    console.log('fileReader.onLoad --> timeStamp:', event.timeStamp);

    onUploadNormalizationCSVFile(event.target.result, data, fileNameRef.current);
    globalAddUndoItem();
  };

  const handleChangeFile = (event) => {
    const fileReader = new FileReader();
    fileReader.onloadstart = (e) => handleLoadStart(e);
    fileReader.onload = (e) => handleLoad(e);

    const fileList = event.target.files;

    if (!fileList || !fileList[0]) {
      return;
    }

    const {name: fileNameLocal} = fileList[0];
    const fileNameLocalArr = fileNameLocal.split('.');

    if (fileNameLocalArr.length > 1 && fileNameLocalArr[fileNameLocalArr.length - 1] !== 'csv') {
      dispatch(showNormalizationInvalidFile());

      return;
    }

    fileNameRef.current = fileNameLocal;
    handleSelectRowColumnIndex(null, null);

    // TODO: check if FileList is empty or not
    // TODO: check if File type is really a csv file

    fileReader.readAsText(fileList[0]);
  };

  const handleClickCell = async (isValid, row, column, isHeader) => {
    if (!isValid) {
      return;
    }
    let newRow = row;
    const newColumn = column;
    if (isHeader) {
      newRow += 1;
    }
    await handleSelectRowColumnIndex(newRow, newColumn);
    if (startRow !== newRow || selectedColumnIndex !== newColumn) {
      globalAddUndoItem();
    }
  };

  const getMaxMinData = () => {
    const {colCount, rowCount} = selectedLabware;
    const wellCount = colCount * rowCount + 1;

    const data = csvData.slice(startRow, wellCount + startRow).map((row) => row[selectedColumnIndex]);

    let maxData = 0;
    let minData = 0;

    if (data.length) {
      maxData = Math.max(...data);
      minData = Math.min(...data);
    }

    return [maxData, minData];
  };

  const generateTableData = () => {
    const elemetns = [];
    if (csvData.length) {
      const selectedTableIndex = {row: startRow, column: selectedColumnIndex};
      const [maxData, minData] = getMaxMinData();
      const {colCount, rowCount} = selectedLabware;
      const wellCount = colCount * rowCount;

      // eslint-disable-next-line no-unsafe-optional-chaining
      const bottomMost = wellCount + selectedTableIndex?.row - 1;

      for (let i = 0; i < csvData.length; i += 1) {
        const cells = [];
        const data = csvData[i];

        for (let j = 0; j < data.length; j += 1) {
          const colData = data[j];
          let isValid = true;
          let isHeader = false;

          if ((Number.isNaN(Number(colData)) && colData !== 'Zero') || colData === Infinity) {
            isValid = false;
          }

          if (i === 0 && Number.isNaN(Number(colData))) {
            isHeader = true;
            hasHederRef.current = true;
            const nextData = csvData[i + 1][j];
            if (!Number.isNaN(Number(nextData))) {
              isValid = true;
            }
          }

          const selectedCell = bottomMost >= i && selectedTableIndex?.row <= i && selectedTableIndex?.column === j;
          const level = (colData + 1 - minData) / (maxData + 1 - minData);

          cells.push(
            <TableCell
              key={`cell-${i}-${j}`}
              className="table-cell"
              ref={tableCellRef}
              onClick={() => handleClickCell(isValid, i, j, isHeader)}
              level={level}
              selected={selectedCell && isValid}
              highlightData={
                selectedLabwareWell && selectedLabwareWell + startRow - 1 === i && selectedTableIndex?.column === j
              }
              isHeader={isHeader}
              isInvalid={!isValid}
              style={{
                borderLeft: `${selectedCell ? '2px solid #338CFF' : '1px solid black'}`,
                borderRight: `${selectedCell ? '2px solid #338CFF' : '1px solid black'}`,
                borderTop: `${selectedCell && selectedTableIndex?.row === i ? '2px solid #338CFF' : '1px solid black'}`,
                borderBottom: `${selectedCell && bottomMost === i ? '2px solid #338CFF' : '1px solid black'}`,
              }}
            >
              {colData}
            </TableCell>,
          );
        }

        elemetns.push(
          <tr key={`header-${i}`} className={`table-row ${i === 0 ? 'row-header' : ''}`}>
            {cells}
          </tr>,
        );
      }
    }

    return elemetns;
  };

  const onChangeDelimeter = (value) => {
    setCurrentDelimiter(value);

    if (!value) {
      return;
    }

    const data = Papa.parse(csvRawData, {
      delimiter: value,
      dynamicTyping: true,
      skipEmptyLines: 'greedy',
    });

    handleChangeDelimiter(value);
    onUploadNormalizationCSVFile(csvRawData, data, fileNameRef.current);
  };

  const handleDownloadCSVFile = () => {
    const link = document.createElement('a');
    link.href = '/images/normalization/Concentration_Normalization_Sample.csv';
    link.download = 'Concentration Normalization Sample.csv';
    link.click();
  };

  const removeNormalizationFile = () => {
    dispatch(removeNormalizationCSVFile());
  };

  return (
    <div className="upload-and-manage-csv-file">
      <div className="upload-and-manage-csv-file__panel">
        <div className="upload-and-manage-csv-file__panel__top-section">
          <div className="upload-and-manage-csv-file__panel__top-section__area">
            <label className="radio-button-container" htmlFor>
              Row : A1,A2,A3,...
              <input
                type="radio"
                name="wellIncrementPattern"
                value={WellIncrementPattern.ORDER_BY_ROW}
                onChange={(event) => handleChangeWellIncrementPattern(event.target.value)}
                checked={wellIncrementPattern === WellIncrementPattern.ORDER_BY_ROW}
              />
              <span className="custom-radio-button" />
            </label>
          </div>
          <div className="upload-and-manage-csv-file__panel__top-section__area">
            <label className="radio-button-container" htmlFor>
              Column : A1,B1,C1,...
              <input
                type="radio"
                name="wellIncrementPattern"
                value={WellIncrementPattern.ORDER_BY_COLUMN}
                onChange={(event) => handleChangeWellIncrementPattern(event.target.value)}
                checked={wellIncrementPattern === WellIncrementPattern.ORDER_BY_COLUMN}
              />
              <span className="custom-radio-button" />
            </label>
          </div>
        </div>

        <div className="upload-and-manage-csv-file__panel__bottom-section">
          <LabwarePreview
            showConcentration
            handleClick={handleClickLabwareWell}
            selectedLabwareWell={selectedLabwareWell}
          />
        </div>
      </div>

      {csvData.length ? (
        <div className="upload-and-manage-csv-file__panel manage-csv-file">
          <div className="manage-csv-file__top-section table-setting">
            <div className="table-setting__upload-file-button upload-file-button">
              <input
                type="file"
                className="upload-file-button__input"
                id="normalization-file"
                accept=".csv"
                onChange={(event) => {
                  handleChangeFile(event);
                }}
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="normalization-file" className="upload-file-button__label">
                <FmlxIcon name="Upload" customColor="#F7A246" size="sm" />
              </label>
              <div className="normalization-file-container">
                <span className="upload-file-button__filename">{fileNameRef.current || csvFileName}</span>
                <div className="remove-file-button-container" onClick={removeNormalizationFile}>
                  <FmlxIcon name="CloseOutline" customColor="#888888" size="xs" />
                </div>
              </div>
            </div>
            <div className="table-setting__column-delimeter">
              <span>Column Delimiter</span>
              <input
                className="table-setting__column-delimeter__input"
                type="text"
                value={currentDelimiter}
                onChange={(event) => onChangeDelimeter(event.target.value)}
              />
            </div>
          </div>
          <p className="normalization-concentration-instruction">
            Choose the concentration column from the uploaded file:
          </p>
          <div ref={tableWrapperRef} className="manage-csv-file__bottom-section table-data">
            <table>
              <tbody>{generateTableData()}</tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="upload-and-manage-csv-file__panel upload-csv-file">
          <img
            src="/images/normalization/sample-normalization.png"
            alt="sample normalization"
            className="sample-normalization"
          />
          <p className="normalization-template-description">
            You can upload a&nbsp;
            <strong>.CSV</strong>
            &nbsp;file if you already have one in the same format.
            <br />
            <strong>Or</strong>, Download the&nbsp;
            <strong>Normalization Template</strong>
            &nbsp;to create the appropriate file.
          </p>
          <div className="normalization-button-section">
            <button className="download-file-button" type="button" onClick={handleDownloadCSVFile}>
              <FmlxIcon name="Download" customColor="#F7A246" size="sm" />
              <span>GET TEMPLATE</span>
            </button>
            <div className="upload-file-button">
              <input
                type="file"
                className="upload-file-button__input"
                id="normalization-file"
                accept=".csv"
                onChange={(event) => {
                  handleChangeFile(event);
                }}
                onClick={(e) => {
                  e.target.value = null;
                }}
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="normalization-file" className="upload-file-button__label upload-csv-file__label">
                <FmlxIcon name="Upload" customColor="white" size="sm" />
                <span>UPLOAD CSV FILE</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

UploadAndManageCSVFile.propTypes = {
  handleChangeWellIncrementPattern: PropTypes.func,
  onUploadNormalizationCSVFile: PropTypes.func,
  appStateAction: PropTypes.object,
  handleSelectRowColumnIndex: PropTypes.func,
  globalAddUndoItem: PropTypes.func,
  handleChangeDelimiter: PropTypes.func,
};

UploadAndManageCSVFile.defaultProps = {
  handleChangeWellIncrementPattern: () => {},
  onUploadNormalizationCSVFile: () => {},
  appStateAction: {},
  handleSelectRowColumnIndex: () => {},
  handleChangeDelimiter: () => {},
  globalAddUndoItem: () => {},
};

export default UploadAndManageCSVFile;
